import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Tooltip, ActionIcon } from "@mantine/core";
import {
  IconChartBar,
  IconChevronLeft,
  IconChevronRight,
  IconHome,
} from "@tabler/icons-react";
import { useGate } from "statsig-react";
import {
  HEADER_HEIGHT,
  NAV_SIDEBAR_WIDTH_COLLAPSED,
  NAV_SIDEBAR_WIDTH_EXPANDED,
} from "@/config/data";
import { NAV_SECTIONS, type NavItem } from "@/config/navigation";
import { useProfileHome } from "@/hooks/useProfileHome";
import { useCoverageBetaEnabled } from "@/hooks/useCoverageBetaEnabled";

interface GlobalNavProps {
  collapsed: boolean;
  onCollapsedChange: (val: boolean) => void;
}

export const GlobalNav: React.FC<GlobalNavProps> = ({
  collapsed,
  onCollapsedChange,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { value: isAdmin } = useGate("out_of_stock_admin");
  const { value: benchmarkEnabled } = useGate("fe_enable_oos_benchmark_quiz");
  const { enabled: coverageBetaEnabled } = useCoverageBetaEnabled();
  const { jobsPath, profileAsHome } = useProfileHome();

  if (location.pathname.startsWith("/jobs/")) {
    return null;
  }

  // The nav tabs filter the jobs table via query params, so "home page" here
  // means "on the jobs table" — which lives at `jobsPath` (`/labeling` when the
  // profile-as-home gate is on, otherwise `/`).
  const isHomePage = location.pathname === jobsPath;
  const activeTab = isHomePage
    ? (searchParams.get("tab") ?? "confirmation")
    : null;

  const handleNavItem = (tabValue: string) => {
    const next = new URLSearchParams(searchParams);
    if (tabValue === "confirmation") {
      next.delete("tab");
    } else {
      next.set("tab", tabValue);
    }
    next.delete("page");
    const dest = next.toString() ? `${jobsPath}?${next.toString()}` : jobsPath;
    if (isHomePage) {
      setSearchParams(next, { replace: true });
    } else {
      navigate(dest);
    }
  };

  const renderNavButton = ({
    key,
    icon,
    label,
    isActive,
    onClick,
  }: {
    key: string;
    icon: React.ReactNode;
    label: string;
    isActive: boolean;
    onClick: () => void;
  }) => {
    const content = (
      <button
        key={key}
        onClick={onClick}
        className={[
          "flex items-center gap-3 w-full text-left transition-colors",
          collapsed ? "justify-center px-0 py-3" : "px-4 py-2.5",
          isActive
            ? "bg-blue-50 text-blue-700 border-r-2 border-blue-600 font-medium"
            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
        ].join(" ")}
      >
        <span className="shrink-0">{icon}</span>
        {!collapsed && <span className="flex-1 text-sm truncate">{label}</span>}
      </button>
    );

    return collapsed ? (
      <Tooltip key={key} label={label} position="right" withArrow>
        {content}
      </Tooltip>
    ) : (
      <React.Fragment key={key}>{content}</React.Fragment>
    );
  };

  const renderNavItem = (item: NavItem) =>
    renderNavButton({
      key: item.value,
      icon: item.icon,
      label: item.label,
      isActive: activeTab === item.value,
      onClick: () => handleNavItem(item.value),
    });

  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      ({ adminOnly, value }) =>
        (adminOnly ? isAdmin : true) &&
        (value === "my-quizzes" || value === "benchmark-admin"
          ? benchmarkEnabled
          : true) &&
        (value === "coverage-beta-qa" || value === "admin-coverage-beta-qa"
          ? coverageBetaEnabled
          : true),
    ),
  })).filter((section) => section.items.length > 0);

  return (
    <nav
      className="fixed left-0 bottom-0 flex flex-col bg-white border-r border-gray-200 z-10 overflow-y-auto overflow-x-hidden transition-all duration-200"
      style={{
        top: HEADER_HEIGHT,
        width: collapsed
          ? NAV_SIDEBAR_WIDTH_COLLAPSED
          : NAV_SIDEBAR_WIDTH_EXPANDED,
      }}
    >
      <div className="flex items-center justify-end p-2 border-b border-gray-100 shrink-0">
        <Tooltip
          label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          position="right"
        >
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => onCollapsedChange(!collapsed)}
          >
            {collapsed ? (
              <IconChevronRight size={16} />
            ) : (
              <IconChevronLeft size={16} />
            )}
          </ActionIcon>
        </Tooltip>
      </div>

      <div className="flex flex-col py-2">
        {profileAsHome && (
          <>
            {renderNavButton({
              key: "home",
              icon: <IconHome size={18} />,
              label: "Home",
              isActive: location.pathname === "/",
              onClick: () => navigate("/"),
            })}
            <div className="mx-3 my-1 border-t border-gray-100" />
          </>
        )}
        {visibleSections.map((section, sectionIndex) => (
          <React.Fragment key={section.title}>
            {sectionIndex > 0 && (
              <div className="mx-3 my-1 border-t border-gray-100" />
            )}
            {!collapsed && (
              <span className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 select-none">
                {section.title}
              </span>
            )}
            {section.items.map(renderNavItem)}
          </React.Fragment>
        ))}
        {isAdmin && (
          <>
            <div className="mx-3 my-1 border-t border-gray-100" />
            {!collapsed && (
              <span className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 select-none">
                Reports
              </span>
            )}
            {renderNavButton({
              key: "admin-metrics",
              icon: <IconChartBar size={18} />,
              label: "Labeler Metrics",
              isActive: location.pathname === "/admin/metrics",
              onClick: () => navigate("/admin/metrics"),
            })}
          </>
        )}
      </div>
    </nav>
  );
};
